// Google Calendar OAuth and API Service
import { supabase, getSupabaseClient } from '../utils/supabaseClient';
// SECURITY: Service key removed - use Edge Functions instead

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = "YOUR_GOOGLE_CLIENT_ID"; // Replace with actual client ID
const GOOGLE_CLIENT_SECRET = "YOUR_GOOGLE_CLIENT_SECRET"; // Replace with actual client secret
const GOOGLE_REDIRECT_URI = `${window.location.origin}/oauth/google/callback`;
const GOOGLE_SCOPE = "https://www.googleapis.com/auth/calendar";

class GoogleCalendarService {
  constructor() {
    // SECURITY: Use Supabase client instead of hardcoded headers
    this.supabase = getSupabaseClient();
  }

  // NOTE: `_getAuthHeaders` and the raw fetch calls against
  // `${SUPABASE_URL}/rest/v1/integration_tokens` were removed when the token
  // read/write paths moved to the supabase client. The helper also fell back to the
  // ANON key when no session existed, sending unauthenticated requests to PostgREST
  // as anon rather than failing early.

  // Initiate OAuth flow
  static initiateOAuth(companyId) {
    const state = btoa(JSON.stringify({ companyId, timestamp: Date.now() }));
    
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: GOOGLE_REDIRECT_URI,
      scope: GOOGLE_SCOPE,
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent',
      state: state
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    window.location.href = authUrl;
  }

  // Exchange authorization code for tokens
  static async exchangeCodeForTokens(code, state) {
    try {
      const { companyId } = JSON.parse(atob(state));
      
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: GOOGLE_REDIRECT_URI,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const tokens = await response.json();
      
      // Store tokens in Supabase
      await this.saveTokens(companyId, tokens);
      
      return { success: true, companyId };
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      throw error;
    }
  }

  // Save tokens to Supabase
  static async saveTokens(companyId, tokens) {
    try {
      // Live schema is (company_id, integration_type, token_data jsonb NOT NULL,
      // is_active, expires_at). The old payload wrote provider/access_token/
      // refresh_token/extra — none exist — so every Google Calendar connect 400'd.
      const expiresAt = tokens.expires_in
        ? new Date(Date.now() + (tokens.expires_in * 1000)).toISOString()
        : null;

      const { error } = await supabase
        .from('integration_tokens')
        .upsert({
          company_id: companyId,
          integration_type: 'google_calendar',
          token_data: {
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
            expires_in: tokens.expires_in,
            token_type: tokens.token_type,
            scope: tokens.scope,
            expires_at: expiresAt
          },
          is_active: true,
          expires_at: expiresAt
        }, { onConflict: 'company_id,integration_type' });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error saving tokens:', error);
      throw error;
    }
  }

  // Get valid access token (refresh if needed)
  static async getValidAccessToken(companyId) {
    try {
      const tokens = await this.getStoredTokens(companyId);
      if (!tokens) {
        throw new Error('No tokens found. Please reconnect Google Calendar.');
      }

      // Secrets live under token_data — reading tokens.extra/.access_token/
      // .refresh_token (the pre-migration shape) yielded undefined, so expiresAt was
      // Invalid Date, the `<=` comparison was false, and an undefined access token
      // was returned as if valid.
      const td = tokens.token_data || {};

      // No expiry recorded: treat as expired and refresh rather than gambling on a
      // possibly-dead token.
      const expiresAt = td.expires_at ? new Date(td.expires_at) : null;
      const now = new Date();

      if (!expiresAt || Number.isNaN(expiresAt.getTime()) || expiresAt <= now) {
        console.log('Access token expired or unknown expiry, refreshing...');
        return await this.refreshAccessToken(companyId, td.refresh_token);
      }

      return td.access_token;
    } catch (error) {
      console.error('Error getting valid access token:', error);
      throw error;
    }
  }

  // Refresh access token
  static async refreshAccessToken(companyId, refreshToken) {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh access token');
      }

      const newTokens = await response.json();
      
      // Update stored tokens
      await this.updateStoredTokens(companyId, newTokens);
      
      return newTokens.access_token;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      throw error;
    }
  }

  // Get stored tokens from Supabase.
  // Returns the row in LIVE shape — secrets are under `.token_data`, not top level.
  static async getStoredTokens(companyId) {
    try {
      const { data, error } = await supabase
        .from('integration_tokens')
        .select('*')
        .eq('company_id', companyId)
        .eq('integration_type', 'google_calendar')
        .maybeSingle();

      if (error) throw error;
      return data || null;
    } catch (error) {
      console.error('Error getting stored tokens:', error);
      return null;
    }
  }

  // Refresh the access token, preserving everything else.
  //
  // ⚠️  REFRESH TOKEN HAZARD: Google issues refresh_token ONCE, on first consent.
  // A refresh response contains a NEW access_token but usually NO refresh_token.
  // Writing the response wholesale would null the stored refresh_token and silently
  // break reconnection until the user revoked and re-authorised. The spread below
  // starts from existing token_data so the refresh_token survives, and the new value
  // is only applied when Google actually returned one.
  static async updateStoredTokens(companyId, newTokens) {
    try {
      const existing = await this.getStoredTokens(companyId);
      if (!existing) {
        throw new Error('No existing tokens to update');
      }

      const expiresAt = newTokens.expires_in
        ? new Date(Date.now() + (newTokens.expires_in * 1000)).toISOString()
        : null;

      const merged = {
        ...(existing.token_data || {}),
        access_token: newTokens.access_token,
        expires_in: newTokens.expires_in,
        expires_at: expiresAt
      };

      if (newTokens.refresh_token) {
        merged.refresh_token = newTokens.refresh_token;
      }

      const { error } = await supabase
        .from('integration_tokens')
        .update({
          token_data: merged,
          is_active: true,
          expires_at: expiresAt
        })
        .eq('id', existing.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating stored tokens:', error);
      throw error;
    }
  }

  // Create test calendar event
  static async createTestEvent(companyId) {
    try {
      const accessToken = await this.getValidAccessToken(companyId);
      
      const event = {
        summary: 'TradeMate Pro Integration Test',
        description: 'This is a test event created by TradeMate Pro to verify Google Calendar integration.',
        start: {
          dateTime: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour from now
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        attendees: [],
        reminders: {
          useDefault: false,
          overrides: [
            { method: 'email', minutes: 24 * 60 },
            { method: 'popup', minutes: 10 },
          ],
        },
      };

      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Google Calendar API error: ${error.error?.message || 'Unknown error'}`);
      }

      const createdEvent = await response.json();
      
      return {
        success: true,
        message: 'Test event created successfully!',
        eventId: createdEvent.id,
        eventUrl: createdEvent.htmlLink
      };
    } catch (error) {
      console.error('Error creating test event:', error);
      
      if (error.message.includes('invalid_grant') || error.message.includes('unauthorized')) {
        return {
          success: false,
          message: 'Authorization expired. Please reconnect Google Calendar.',
          requiresReauth: true
        };
      }
      
      return {
        success: false,
        message: `Failed to create test event: ${error.message}`
      };
    }
  }

  // Test connection
  static async testConnection(companyId) {
    try {
      const tokens = await this.getStoredTokens(companyId);
      if (!tokens) {
        return {
          success: false,
          message: 'No Google Calendar connection found. Please connect first.'
        };
      }

      // Try to get calendar list to test connection
      const accessToken = await this.getValidAccessToken(companyId);
      
      const response = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to access Google Calendar API');
      }

      const calendars = await response.json();
      
      return {
        success: true,
        message: `Connected to Google Calendar! Found ${calendars.items?.length || 0} calendars.`,
        calendars: calendars.items
      };
    } catch (error) {
      console.error('Error testing Google Calendar connection:', error);
      
      if (error.message.includes('invalid_grant') || error.message.includes('unauthorized')) {
        return {
          success: false,
          message: 'Authorization expired. Please reconnect Google Calendar.',
          requiresReauth: true
        };
      }
      
      return {
        success: false,
        message: `Connection test failed: ${error.message}`
      };
    }
  }

  // Disconnect (delete tokens). Filtered on `provider`, which is not a column, so
  // disconnect 400'd and the stored tokens were never actually removed.
  static async disconnect(companyId) {
    try {
      const { error } = await supabase
        .from('integration_tokens')
        .delete()
        .eq('company_id', companyId)
        .eq('integration_type', 'google_calendar');

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      return false;
    }
  }
}

export default GoogleCalendarService;
